import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Dialog, DialogContent, DialogTitle } from '@mui/material'
import BackHeader from '../../components/BackHeader/BackHeader'
import { formatDay } from './logic'
import { useHold } from './store'
import Loading from '../../components/Loading/Loading'
import { HoldScreen, SeasonSwitch } from './bits'

function phoneDigits(value) {
  return String(value || '').replace(/\D/g, '')
}

function phoneLabel(digits) {
  if (digits.length !== 8) return digits
  return digits.replace(/(\d{2})(?=\d)/g, '$1 ').trim()
}

function openMobilePay() {
  const android = /Android/i.test(navigator.userAgent)
  const url = android
    ? 'intent://open/#Intent;scheme=mobilepay;package=dk.danskebank.mobilepay;end'
    : 'mobilepay://'
  const opened = window.open(url, '_blank')
  if (!opened) window.location.assign(url)
}

const feeDialogPaper = {
  className: 'hold-create-paper',
  sx: {
    backgroundColor: '#221c24',
    color: '#f3eadf',
    border: '1px solid rgba(255,255,255,0.16)',
    borderRadius: '18px',
    boxShadow: '0 24px 60px rgba(0, 0, 0, 0.55)',
  },
}

const feeDialogBackdrop = {
  sx: {
    backdropFilter: 'blur(8px)',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
  },
}

export function FeePayDialog({ open, onClose, mobilePay, amount }) {
  const [copied, setCopied] = useState(false)
  const phone = phoneDigits(mobilePay)

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={feeDialogPaper}
      BackdropProps={feeDialogBackdrop}
      TransitionProps={{ onExited: () => setCopied(false) }}
    >
      <DialogTitle sx={{ color: '#f7f4ef', fontWeight: 800, paddingBottom: 0 }}>Betal kontingent</DialogTitle>
      <DialogContent>
        <div className="hold-fee-pay">
          <p className="hold-meta">Telefonnummer</p>
          {phone ? (
            <div className="hold-fee-phone">
              <strong>{phoneLabel(phone)}</strong>
              <button
                type="button"
                className="hold-action hold-action--small hold-action--save"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(phone)
                    setCopied(true)
                  } catch {
                    setCopied(false)
                  }
                }}
              >
                {copied ? 'Kopieret' : 'Kopier'}
              </button>
            </div>
          ) : (
            <p className="hold-note">Holdet har ikke sat et MobilePay-nummer.</p>
          )}
          <p className="hold-meta">Pris</p>
          <p className="hold-fee-price">{amount} kr</p>
          <button type="button" className="hold-action" onClick={openMobilePay}>Åbn MobilePay</button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export function teamIdFromPath(pathname) {
  const id = String(pathname || '').match(/^\/hold\/([^/]+)/)?.[1]
  if (!id || id === 'inviter' || id === 'tilmeld') return null
  return decodeURIComponent(id)
}

export function FeeReminder({ team, season, meId, active }) {
  const teamId = active && team ? team.id : null
  const [dismissedId, setDismissedId] = useState(null)
  const [payOpen, setPayOpen] = useState(false)
  if (!teamId && dismissedId) setDismissedId(null)
  if (!teamId && payOpen) setPayOpen(false)

  const member = Boolean(team?.members.some((item) => item.id === meId))
  const unpaid = Boolean(
    teamId
    && season
    && member
    && (season.fee.amount || 0) > 0
    && !season.fee.paid[meId]
  )

  return (
    <>
      <Dialog
        open={unpaid && dismissedId !== teamId && !payOpen}
        onClose={() => setDismissedId(teamId)}
        maxWidth="xs"
        fullWidth
        PaperProps={feeDialogPaper}
        BackdropProps={feeDialogBackdrop}
      >
        <DialogTitle sx={{ color: '#f7f4ef', fontWeight: 800, paddingBottom: 0 }}>Kontingent</DialogTitle>
        <DialogContent>
          <div className="hold-fee-pay">
            <p className="hold-copy">Du mangler at betale kontingent.</p>
            <p className="hold-fee-price">{season?.fee.amount} kr</p>
            {season?.fee.due ? <p className="hold-meta">Senest {formatDay(season.fee.due)}</p> : null}
            <button
              type="button"
              className="hold-action"
              onClick={() => {
                setDismissedId(teamId)
                setPayOpen(true)
              }}
            >
              Betal med MobilePay/Vipps
            </button>
          </div>
        </DialogContent>
      </Dialog>
      <FeePayDialog
        open={payOpen}
        onClose={() => setPayOpen(false)}
        mobilePay={team?.mobilePay}
        amount={season?.fee.amount}
      />
    </>
  )
}

export default function FeeView() {
  const navigate = useNavigate()
  const { team, season, isAdmin, dispatch, nameOf, meId, loading } = useHold()
  const [payOpen, setPayOpen] = useState(false)
  if (loading && !team) return <HoldScreen><Loading fill /></HoldScreen>
  if (!team || !season) return null
  const paid = Boolean(season.fee.paid[meId])

  return (
    <HoldScreen>
      <BackHeader eyebrow={team.name} title="Kontingent" subtitle={season.name} onBack={() => navigate(`/hold/${team.id}`)} />
      <SeasonSwitch team={team} season={season} dispatch={dispatch} />
      <section className={`hold-fee ${paid ? 'is-paid' : ''}`}>
        <p className="hold-meta">{paid ? 'Du har betalt' : 'Du mangler at betale'}</p>
        <strong>{season.fee.amount} kr</strong>
        <p className="hold-meta">Senest {formatDay(season.fee.due)}</p>
        <button type="button" className="hold-action" onClick={() => setPayOpen(true)}>
          Betal med MobilePay/Vipps
        </button>
      </section>
      <FeePayDialog
        open={payOpen}
        onClose={() => setPayOpen(false)}
        mobilePay={team.mobilePay}
        amount={season.fee.amount}
      />
      <ul className="hold-fee-list">
        {team.members.map((member) => {
          const hasPaid = Boolean(season.fee.paid[member.id])
          return (
            <li key={member.id}>
              <span>{nameOf(member.id)}</span>
              {isAdmin ? (
                <button
                  type="button"
                  className={hasPaid ? 'is-paid' : ''}
                  onClick={() => dispatch({
                    type: 'TOGGLE_PAID',
                    teamId: team.id,
                    seasonId: season.id,
                    playerId: member.id,
                  })}
                >
                  {hasPaid ? 'Betalt' : 'Mangler'}
                </button>
              ) : (
                <span className={hasPaid ? 'is-paid' : 'is-missing'}>{hasPaid ? 'Betalt' : 'Mangler'}</span>
              )}
            </li>
          )
        })}
      </ul>
    </HoldScreen>
  )
}
